
import { useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";
import { AuthFormInput } from "./AuthFormInput";
import { AuthFormButton } from "./AuthFormButton";
import { LinkedInAuth } from "./LinkedInAuth";
import { Separator } from "@/components/ui/separator";

interface AuthFormProps {
  mode: "signin" | "signup" | "reset" | "update";
  selectedPlan?: string;
}

export const AuthForm = ({ mode, selectedPlan }: AuthFormProps) => {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const navigate = useNavigate();
  const { toast } = useToast();
  const [searchParams] = useSearchParams();
  const wantsTrial = searchParams.get('trial') === 'true';

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsLoading(true);

    try {
      if (mode === "signup") {
        if (password !== confirmPassword) {
          throw new Error("Passwords don't match");
        }

        const { data, error } = await supabase.auth.signUp({
          email,
          password,
          options: {
            emailRedirectTo: `${window.location.origin}/dashboard`,
            data: {
              selected_plan: selectedPlan,
              wants_trial: wantsTrial,
            },
          },
        });

        if (error) throw error;

        // If user wants trial and signup was successful, create trial
        if (wantsTrial && data.user) {
          try {
            const { error: trialError } = await supabase
              .from('free_trials')
              .insert({
                user_id: data.user.id,
                email: email,
              });

            if (!trialError) {
              toast({
                title: "Account created with Free Trial!",
                description: "Your 24-hour trial has started. Please check your email to verify your account.",
                className: "bg-green-50 border-green-200",
              });
              navigate('/dashboard');
              return;
            }
          } catch (trialError) {
            console.error('Trial creation failed:', trialError);
          }
        }

        toast({
          title: "Account created!",
          description: "Please check your email to verify your account.",
        });
      } else if (mode === "signin") {
        // Check for demo credentials
        if (email === "demo@csoagent.com" && password === "showcase2024") {
          console.log("Demo login detected, creating demo session...");
          
          try {
            // Use a persistent demo email that won't need verification
            const demoEmail = 'demo-user@csoagent.com';
            const demoPassword = 'DemoPassword123!';

            // Try to sign in with existing demo account first
            let authResult = await supabase.auth.signInWithPassword({
              email: demoEmail,
              password: demoPassword
            });

            if (authResult.error) {
              console.log("Demo account doesn't exist, creating it...");
              
              // Create demo account without email confirmation
              const { data: signUpData, error: signUpError } = await supabase.auth.signUp({
                email: demoEmail,
                password: demoPassword,
                options: {
                  data: {
                    full_name: 'Demo User',
                    is_demo: true,
                    email_confirmed_at: new Date().toISOString()
                  }
                }
              });

              if (signUpError) {
                console.error("Demo signup error:", signUpError);
                throw new Error('Failed to create demo account.');
              }

              // Try signing in again after creation
              authResult = await supabase.auth.signInWithPassword({
                email: demoEmail,
                password: demoPassword
              });

              if (authResult.error) {
                throw new Error('Demo account created but sign-in failed.');
              }
            }

            // Verify we have a session
            const { data: { session } } = await supabase.auth.getSession();
            
            if (!session?.user) {
              throw new Error('Failed to establish demo session');
            }

            console.log("Demo session established for user:", session.user.id);

            toast({
              title: "Demo Access Granted!",
              description: "You now have full enterprise access for showcasing.",
              className: "bg-green-50 border-green-200",
            });

            navigate('/dashboard');
            return;
          } catch (demoError) {
            console.error('Demo login failed:', demoError);
            toast({
              title: "Demo Login Failed",
              description: "Please try again or contact support.",
              variant: "destructive",
            });
          }
        }

        // Regular sign in
        const { error } = await supabase.auth.signInWithPassword({
          email,
          password,
        });

        if (error) throw error;
        
        // Don't navigate here - let the auth state change handler do it
      } else if (mode === "reset") {
        const { error } = await supabase.auth.resetPasswordForEmail(email, {
          redirectTo: `${window.location.origin}/auth?mode=update`,
        });

        if (error) throw error;

        toast({
          title: "Reset email sent!",
          description: "Check your email for the password reset link.",
        });
      } else if (mode === "update") {
        const { error } = await supabase.auth.updateUser({ password });

        if (error) throw error;

        toast({
          title: "Password updated!",
          description: "Your password has been updated successfully.",
        });
        navigate("/dashboard");
      }
    } catch (error: any) {
      console.error("Auth error:", error);
      toast({
        title: "Error",
        description: error.message,
        variant: "destructive",
      });
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="space-y-6">
      {/* LinkedIn Auth */}
      {(mode === "signin" || mode === "signup") && (
        <>
          <LinkedInAuth />
          <div className="relative">
            <div className="absolute inset-0 flex items-center">
              <Separator className="w-full" />
            </div>
            <div className="relative flex justify-center text-xs uppercase">
              <span className="bg-gray-50 px-2 text-gray-500">Or continue with email</span>
            </div>
          </div>
        </>
      )}

      <form onSubmit={handleSubmit} className="space-y-4">
        <AuthFormInput
          id="email"
          type="email"
          value={email}
          onChange={setEmail}
          required
          mode={mode}
        />

        {mode !== "reset" && (
          <AuthFormInput
            id="password"
            type="password"
            value={password}
            onChange={setPassword}
            required
            mode={mode}
          />
        )}

        {mode === "signup" && (
          <AuthFormInput
            id="confirmPassword"
            type="password"
            value={confirmPassword}
            onChange={setConfirmPassword}
            required
            mode={mode}
          />
        )}

        <AuthFormButton mode={mode} loading={isLoading} />
      </form>
    </div>
  );
};
